package com.schoolconnect.app.feature.classconversations

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.Image
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.downloadFile
import com.schoolconnect.app.util.decodeImageBitmap
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerMode
import io.github.vinceglb.filekit.core.PickerType
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.feature.common.*
import com.schoolconnect.app.feature.parentnotes.ParentConversationDialog
import com.schoolconnect.app.feature.parentnotes.ParentNoteRowUi
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.cloud_upload
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.download
import ischoolconnect.sharedui.generated.resources.eye
import ischoolconnect.sharedui.generated.resources.lock
import ischoolconnect.sharedui.generated.resources.more_vertical
import ischoolconnect.sharedui.generated.resources.message
import ischoolconnect.sharedui.generated.resources.plus
import ischoolconnect.sharedui.generated.resources.paperclip
import ischoolconnect.sharedui.generated.resources.refresh_cw
import ischoolconnect.sharedui.generated.resources.search
import ischoolconnect.sharedui.generated.resources.trash
import ischoolconnect.sharedui.generated.resources.pencil
import org.jetbrains.compose.resources.painterResource

private data class ClassConversation(
    val id: String,
    val title: String,
    val postedBy: String,
    val postedOn: String,
    val replies: Int,
    val locked: Boolean = false,
    val classLabel: String = "5-A",
    val closed: Boolean = false,
    val backendId: String? = null,
    val attachmentUrl: String? = null,
    val attachmentFilename: String? = null,
    val attachmentSizeBytes: Int? = null,
    val authorId: String? = null
)

@Composable
fun ClassConversationsScreen(
    shellRoleCode: String,
    onChangeRole: () -> Unit,
    onLogout: () -> Unit,
    adminMode: Boolean = false,
    apiState: ClassConversationsUiState? = null,
    onApiEvent: ((ClassConversationsUiEvent) -> Unit)? = null,
    classGroupLabel: String = "Class 5-A"
) {
    var profileExpanded by remember { mutableStateOf(false) }
    var rowsMenu by remember { mutableStateOf(false) }
    var viewing by remember { mutableStateOf<ClassConversation?>(null) }
    var viewingApiId by remember { mutableStateOf<String?>(null) }
    var startingConversation by remember { mutableStateOf(false) }
    var editingConversation by remember { mutableStateOf<ClassConversation?>(null) }
    var deleteCandidate by remember { mutableStateOf<ClassConversation?>(null) }
    var moderatingConversation by remember { mutableStateOf<ClassConversation?>(null) }
    var classFilter by remember { mutableStateOf("All Classes") }
    var sectionFilter by remember { mutableStateOf("All Sections") }
    var customRangeVisible by remember { mutableStateOf(false) }

    val currentUserId = UserSessionStore.getUserId().trim()
    fun isOwnedByCurrentParent(conversation: ClassConversation): Boolean =
        currentUserId.isNotBlank() && conversation.authorId?.trim()?.equals(currentUserId, ignoreCase = true) == true

    val pageRows = apiState?.rows.orEmpty().map { row ->
        ClassConversation(row.conversationCode ?: row.id ?: "—", row.title ?: "Untitled conversation", row.authorName ?: "—", formatApiConversationDate(row.postedOn ?: row.createdAt), row.replyCount ?: 0, classLabel = apiConversationClassLabel(row.className, row.sectionName), closed = row.status.equals("Closed", true), backendId = row.id, attachmentUrl = row.attachmentUrl, attachmentFilename = row.attachmentFilename, attachmentSizeBytes = row.attachmentSizeBytes, authorId = row.authorId)
    }
    val displayPageIndex = apiState?.pageIndex ?: 0
    val displayPageSize = apiState?.pageSize ?: PaginationPolicy.DEFAULT_PAGE_SIZE
    val displayTotal = apiState?.totalCount ?: 0
    val displayPageCount = pageWindow(displayPageIndex, displayPageSize, displayTotal).pageCount

    LaunchedEffect(editingConversation?.backendId, apiState?.selected?.id, apiState?.selected?.authorId, currentUserId) {
        val editingId = editingConversation?.backendId
        val selected = apiState?.selected
        if (editingId != null && selected?.id == editingId && selected.authorId?.trim()?.equals(currentUserId, ignoreCase = true) != true) {
            editingConversation = null
            onApiEvent?.invoke(ClassConversationsUiEvent.ViewDismissed)
        }
    }

    AdminListPageScaffold(
        title = if (adminMode) "Conversation List & Moderation" else "Class Conversations",
        subtitle = if (adminMode) "View and moderate parent conversations across classes." else "Ask questions and connect with parents from your child's class.",
        secondaryHeader = null,
        headerActions = {
            ShellTopRightActions(
                shellRoleLabel(shellRoleCode),
                shellRoleSubtitle(shellRoleCode),
                shellAvatarInitials(shellRoleLabel(shellRoleCode)),
                profileExpanded,
                { profileExpanded = it },
                onChangeRole,
                onLogout
            )
        },
        summaryCards = {},
        toolbar = {
            ClassConversationFilters(
                search = apiState?.query.orEmpty(),
                period = apiState?.period?.removeSuffix(" Time")?.ifBlank { "All" } ?: "All",
                adminMode = adminMode, classFilter = classFilter, sectionFilter = sectionFilter,
                onSearchChanged = { onApiEvent?.invoke(ClassConversationsUiEvent.QueryChanged(it)) },
                onPeriodChanged = {
                    if (it == "Custom Range") customRangeVisible = true
                    else {
                        onApiEvent?.invoke(ClassConversationsUiEvent.PeriodChanged(if (it == "All") CONVERSATION_PERIOD_ALL else it))
                    }
                },
                onClassChanged = { classFilter = it },
                onSectionChanged = { sectionFilter = it },
                onClear = {
                    classFilter = "All Classes"
                    sectionFilter = "All Sections"
                    onApiEvent?.invoke(ClassConversationsUiEvent.FiltersReset)
                }
            )
        },
        table = { modifier ->
            AdminListTableCard(
                modifier = modifier,
                minContentWidth = 1180.dp,
                showVerticalScrollbar = false,
                header = {
                    if (apiState != null && onApiEvent != null) ClassConversationApiHeader(apiState, adminMode, onApiEvent)
                    else ClassConversationHeader(adminMode)
                },
                body = {
                    if (pageRows.isEmpty()) {
                        Box(
                            Modifier.fillMaxWidth().heightIn(min = 220.dp).padding(32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Icon(painterResource(Res.drawable.message), contentDescription = null, tint = AppPrimary, modifier = Modifier.size(36.dp))
                                AdminListEmptyState(
                                    title = if (apiState?.query.isNullOrBlank()) "No conversations yet" else "No conversations found",
                                    subtitle = if (apiState?.query.isNullOrBlank()) "Start a conversation to ask a question about your child's class." else "Try a different conversation ID or title."
                                )
                            }
                        }
                    } else AdminListTableBody(items = pageRows, key = { it.id }, modifier = Modifier.fillMaxWidth()) { row ->
                        Row(
                            modifier = Modifier.fillMaxWidth().clickable {
                                if (apiState != null && onApiEvent != null) {
                                    val conversationId = row.backendId ?: row.id
                                    viewingApiId = conversationId
                                    onApiEvent(ClassConversationsUiEvent.ViewRequested(conversationId))
                                } else {
                                    viewing = row
                                }
                            }.heightIn(min = 52.dp).padding(horizontal = 24.dp, vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            AdminListTableBodyText(row.id, Modifier.weight(1.05f).padding(end = 8.dp), color = AppPrimary, fontWeight = FontWeight.Medium)
                            Row(Modifier.weight(1.7f).padding(end = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text(row.title, color = AppNavy, fontSize = 14.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                if (row.locked) Icon(painterResource(Res.drawable.lock), "Private conversation", tint = AppNavy, modifier = Modifier.size(16.dp))
                                if (adminMode && row.closed) {
                                    Surface(color = Color(0xFFF1F6FF), shape = RoundedCornerShape(4.dp), border = BorderStroke(1.dp, Color(0xFF9DBBFF))) {
                                        Text("Closed", color = AppPrimary, fontSize = 11.sp, modifier = Modifier.padding(horizontal = 5.dp, vertical = 2.dp))
                                    }
                                }
                            }
                            if (adminMode) AdminListTableBodyText(row.classLabel, Modifier.weight(.75f).padding(end = 8.dp), fontWeight = FontWeight.Medium)
                            AdminListTableBodyText(row.postedBy, Modifier.weight(1.6f).padding(end = 16.dp), fontWeight = FontWeight.Medium)
                            AdminListTableBodyText(row.postedOn, Modifier.weight(1.25f).padding(end = 8.dp), fontWeight = FontWeight.Medium)
                            Box(Modifier.weight(.8f), contentAlignment = Alignment.Center) { AdminListTableBodyText(row.replies.toString(), Modifier, fontWeight = FontWeight.Medium) }
                            AdminListTableBodyText(if (row.closed) "CLOSED" else "OPEN", Modifier.weight(.7f).padding(end = 8.dp), color = if (row.closed) AppMuted else AppSuccess)
                            Box(Modifier.weight(.7f), contentAlignment = Alignment.Center) {
                                var expanded by remember { mutableStateOf(false) }
                                AdminListActionIcons(
                                    onView = { expanded = false; if (apiState != null && onApiEvent != null) { viewingApiId = row.backendId ?: row.id; onApiEvent(ClassConversationsUiEvent.ViewRequested(row.backendId ?: row.id)) } else viewing = row },
                                    onEdit = if (adminMode) null else { {
                                        expanded = false
                                        if (isOwnedByCurrentParent(row)) {
                                            editingConversation = row
                                            if (apiState != null) onApiEvent?.invoke(ClassConversationsUiEvent.ViewRequested(row.backendId ?: row.id))
                                        }
                                    } },
                                    editEnabled = adminMode || isOwnedByCurrentParent(row),
                                    editDisabledDescription = "Only the parent who started this conversation can edit it"
                                ) {
                                    val canDelete = adminMode || isOwnedByCurrentParent(row)
                                    if (adminMode || canDelete) {
                                        androidx.compose.material3.IconButton(onClick = { expanded = true }, modifier = Modifier.size(32.dp)) { Icon(painterResource(Res.drawable.more_vertical), "More actions", tint = AppNavy, modifier = Modifier.size(16.dp)) }
                                        AdminRowActionMenu(
                                            sections = buildList {
                                                if (adminMode) add(listOf(AdminRowActionItem("Moderate Conversation", { expanded = false; moderatingConversation = row }, painterResource(Res.drawable.lock))))
                                                if (adminMode) add(listOf(AdminRowActionItem("Close Conversation", { expanded = false; moderatingConversation = row }, painterResource(Res.drawable.close_x))))
                                                if (canDelete) add(listOf(AdminRowActionItem("Delete Conversation", { expanded = false; deleteCandidate = row }, painterResource(Res.drawable.trash), labelColor = AppDanger, iconTint = AppDanger)))
                                            },
                                            onDismissRequest = { expanded = false },
                                            expanded = expanded,
                                            width = 220.dp
                                        )
                                    }
                                }
                            }
                        }
                        HorizontalDivider(color = AppBorder)
                    }
                },
                footer = {
                    AdminListFooter(
                        pageText = pageFooterText(pageWindow(displayPageIndex, displayPageSize, displayTotal), "conversations"),
                        pageIndex = displayPageIndex,
                        pageCount = displayPageCount,
                        onPageChanged = { onApiEvent?.invoke(ClassConversationsUiEvent.PageChanged(it)) },
                        pageSize = displayPageSize,
                        rowsPerPageExpanded = rowsMenu,
                        onRowsPerPageToggle = { rowsMenu = !rowsMenu },
                        onRowsPerPageDismiss = { rowsMenu = false },
                        onPageSizeChanged = { onApiEvent?.invoke(ClassConversationsUiEvent.PageSizeChanged(it)) }
                    )
                }
            )
        },
        bottomAction = if (!adminMode) ({
            AdminListPrimaryActionButton("Start Conversation", painterResource(Res.drawable.plus), { startingConversation = true }, Modifier.width(206.dp).height(46.dp))
        }) else ({})
    )

    if (customRangeVisible) {
        AppDialogDatePickerDialog(
            value = apiState?.periodFrom.orEmpty(),
            onDismiss = { customRangeVisible = false },
            onDateSelected = {},
            colors = AppDialogDefaultColors,
            dateRangeSelection = true,
            dateRangeStartValue = apiState?.periodFrom.orEmpty(),
            dateRangeEndValue = apiState?.periodTo.orEmpty(),
            onDateRangeSelected = { from, to ->
                customRangeVisible = false
                onApiEvent?.invoke(ClassConversationsUiEvent.CustomPeriodChanged(from, to))
            }
        )
    }

    viewing?.let { row ->
        ClassConversationViewDialog(row = row, adminMode = adminMode, onDismiss = { viewing = null })
    }
    if (viewingApiId != null && apiState != null && onApiEvent != null) {
        ApiClassConversationViewDialog(
            detail = apiState.selected,
            replies = apiState.replies,
            isLoading = apiState.isDetailLoading,
            errorMessage = apiState.detailErrorMessage,
            isMutating = apiState.isMutating,
            mutationError = apiState.mutationErrorMessage,
            onDismiss = { viewingApiId = null; onApiEvent(ClassConversationsUiEvent.ViewDismissed) },
            replyResetGeneration = apiState.replyResetGeneration,
            onReply = { text, url, filename -> onApiEvent(ClassConversationsUiEvent.ReplyRequested(viewingApiId!!, text, url, filename)) }
        )
    }

    if (startingConversation) StartConversationDialog(classGroupLabel = classGroupLabel, onDismiss = { startingConversation = false }, onSubmit = { title, description, url, filename, size, _ -> onApiEvent?.invoke(ClassConversationsUiEvent.CreateRequested(title, description, url, filename, size)); startingConversation = false })
    editingConversation?.let { row ->
        val conversationId = row.backendId ?: row.id
        val detail = apiState?.selected?.takeIf { it.id == conversationId }
        if ((apiState == null || detail != null) && (apiState == null || detail?.authorId?.trim()?.equals(currentUserId, ignoreCase = true) == true)) {
            StartConversationDialog(
                initial = row,
                initialDetail = detail,
                classGroupLabel = apiState?.classSectionOptions?.firstOrNull { it.id == detail?.classSectionId }?.label ?: classGroupLabel,
                onDismiss = { editingConversation = null; if (apiState != null) onApiEvent?.invoke(ClassConversationsUiEvent.ViewDismissed) },
                onSubmit = { title, description, url, filename, size, changed ->
                    onApiEvent?.invoke(ClassConversationsUiEvent.UpdateRequested(conversationId, title, description, url, filename, size, changed))
                    editingConversation = null
                    onApiEvent?.invoke(ClassConversationsUiEvent.ViewDismissed)
                }
            )
        } else {
            EditConversationDetailLoadingDialog(
                isLoading = apiState.isDetailLoading,
                errorMessage = apiState.detailErrorMessage,
                onRetry = { onApiEvent?.invoke(ClassConversationsUiEvent.ViewRequested(conversationId)) },
                onDismiss = { editingConversation = null; onApiEvent?.invoke(ClassConversationsUiEvent.ViewDismissed) }
            )
        }
    }
    deleteCandidate?.let { row ->
        ConversationDeleteDialog(
            conversationId = row.backendId ?: row.id,
            conversationCode = row.id,
            title = row.title,
            isMutating = apiState?.isMutating == true,
            onDismiss = { deleteCandidate = null },
            onConfirm = { reason ->
                if (adminMode || isOwnedByCurrentParent(row)) onApiEvent?.invoke(ClassConversationsUiEvent.DeleteRequested(row.backendId ?: row.id, reason))
                deleteCandidate = null
            }
        )
    }
    moderatingConversation?.let { row ->
        AdminModerationDialog(row = row, onDismiss = { moderatingConversation = null })
    }
}

@Composable
internal fun ConversationDeleteDialog(
    conversationId: String,
    conversationCode: String,
    title: String,
    isMutating: Boolean,
    onDismiss: () -> Unit,
    onConfirm: (String?) -> Unit
) {
    var reason by remember(conversationId) { mutableStateOf("") }
    AppConfirmationDialog(
        onDismiss = onDismiss,
        title = "Delete conversation?",
        subtitle = conversationCode,
        bodyText = "This conversation and its replies will be deleted. This action cannot be undone.",
        confirmLabel = if (isMutating) "Deleting…" else "Delete",
        confirmEnabled = !isMutating,
        dismissEnabled = !isMutating,
        tone = AppConfirmationDialogTone.Danger,
        onConfirm = { onConfirm(reason.trim().takeIf { it.isNotBlank() }) },
        content = {
            AppDialogFieldColumn("Reason", helperText = "Optional", colors = AppDialogDefaultColors) {
                AppDialogTextField(
                    value = reason,
                    onValueChange = { reason = it },
                    placeholder = "Add a reason for deleting this conversation",
                    colors = AppDialogDefaultColors,
                    singleLine = false,
                    fieldHeight = 80.dp,
                    maxLength = 500
                )
            }
        }
    )
}

@Composable
private fun AdminModerationDialog(row: ClassConversation, onDismiss: () -> Unit) {
    var note by remember(row.id) { mutableStateOf("") }
    val replies = listOf("Priya Sharma" to "I think it should be submitted on a worksheet. Please confirm.", "Rahul Verma" to "I submitted it in my notebook last week. Just checking.", "Neha Kapoor" to "Please submit it on a worksheet. Thank you.")
    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, maxDialogHeight = 820.dp) {
        AppDialogHeader(title = "Moderate Conversation", subtitle = "${row.id}  ·  Review and manage this conversation", onClose = onDismiss, icon = Res.drawable.lock, iconContentDescription = "Moderation", colors = AppDialogDefaultColors)
        Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(horizontal = 24.dp, vertical = 14.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Column(Modifier.fillMaxWidth().border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp)).padding(14.dp), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                Text("Homework submission clarification", color = AppNavy, fontSize = 15.sp, fontWeight = FontWeight.Bold)
                Text("Posted by Ananya Mehta  •  31 Jul 2026, 08:42 PM  •  Class 5-A", color = AppMuted, fontSize = 11.sp)
                Text("Could someone please confirm whether tomorrow's Mathematics homework must be submitted in the notebook or on a worksheet?", color = AppNavy, fontSize = 12.sp)
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) { Icon(painterResource(Res.drawable.paperclip), null, tint = AppMuted, modifier = Modifier.size(15.dp)); Text("Maths_Homework.pdf", color = AppPrimary, fontSize = 11.sp, fontWeight = FontWeight.SemiBold); Spacer(Modifier.weight(1f)); Text("Delete Conversation", color = AppDanger, fontSize = 11.sp, fontWeight = FontWeight.SemiBold) }
            }
            AppDialogSectionLabel("Replies (3)", colors = AppDialogDefaultColors, fontSize = 15.sp)
            replies.forEach { (sender, message) ->
                Row(Modifier.fillMaxWidth().border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp)).padding(10.dp), verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Box(Modifier.size(30.dp).background(Color(0xFFE7EDFF), RoundedCornerShape(50)), contentAlignment = Alignment.Center) { Text(sender.split(" ").mapNotNull { it.firstOrNull() }.joinToString(""), color = AppPrimary, fontSize = 10.sp, fontWeight = FontWeight.Bold) }
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) { Text(sender, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Bold); Text("31 Jul 2026, 08:45 PM  •  Class 5-A", color = AppMuted, fontSize = 10.sp); Text(message, color = AppNavy, fontSize = 11.sp) }
                    Icon(painterResource(Res.drawable.trash), "Delete reply", tint = AppDanger, modifier = Modifier.size(17.dp))
                }
            }
            AppDialogFieldColumn("Moderation Reason", helperText = "Optional", colors = AppDialogDefaultColors) { AppDialogTextField(note, { note = it }, "Add a reason when deleting content or closing the conversation", colors = AppDialogDefaultColors, singleLine = false, fieldHeight = 70.dp, maxLength = 500) }
        }
        AppDialogFooter(actions = listOf(AppDialogFooterAction("Delete Conversation", onDismiss, AppDialogActionStyle.Danger, width = 240.dp), AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Secondary, width = 120.dp), AppDialogFooterAction("Close Conversation", onDismiss, AppDialogActionStyle.Primary, width = 240.dp)), colors = AppDialogDefaultColors, equalWeightButtons = false)
    }
}

@Composable
internal fun ApiClassConversationViewDialog(
    detail: com.schoolconnect.app.datasource.network.response.ClassConversationResponse?,
    replies: List<com.schoolconnect.app.datasource.network.response.ClassConversationReplyResponse>,
    isLoading: Boolean,
    errorMessage: String,
    isMutating: Boolean,
    mutationError: String,
    replyResetGeneration: Int,
    onDismiss: () -> Unit,
    onReply: (String, String?, String?) -> Unit
) {
    var reply by remember(detail?.id) { mutableStateOf("") }
    var attachmentUrl by remember(detail?.id) { mutableStateOf<String?>(null) }
    var attachmentFilename by remember(detail?.id) { mutableStateOf<String?>(null) }
    var attachmentPreviewBytes by remember(detail?.id) { mutableStateOf<ByteArray?>(null) }
    var uploading by remember(detail?.id) { mutableStateOf(false) }
    var uploadError by remember(detail?.id) { mutableStateOf("") }
    var uploadSession by remember(detail?.id) { mutableStateOf(FileUploadRepository.newSession("CLASS_CONVERSATION_REPLY").copy(recordId = detail?.id)) }
    val coroutineScope = rememberCoroutineScope()
    fun downloadAttachment(url: String, filename: String) {
        coroutineScope.launch {
            FileUploadRepository.downloadBytes(url).onSuccess { bytes ->
                downloadFile(filename, bytes, conversationContentType(filename))
            }
        }
    }
    androidx.compose.runtime.LaunchedEffect(replyResetGeneration) {
        reply = ""
        attachmentUrl = null
        attachmentFilename = null
        attachmentPreviewBytes = null
        uploadError = ""
        uploadSession = FileUploadRepository.newSession("CLASS_CONVERSATION_REPLY").copy(recordId = detail?.id)
    }
    val attachmentPicker = rememberFilePickerLauncher(
        type = PickerType.File(listOf("jpg", "jpeg", "png")),
        mode = PickerMode.Multiple(maxItems = 1),
        title = "Select reply attachment"
    ) { files ->
        val file = files?.firstOrNull()
        if (file != null) coroutineScope.launch {
            uploading = true
            uploadError = ""
            val bytes = runCatching { file.readBytes() }.getOrNull()
            if (bytes == null || bytes.isEmpty()) uploadError = "Unable to read ${file.name}."
            else if (bytes.size > 5 * 1024 * 1024) uploadError = "Attachment must be 5 MB or smaller."
            else FileUploadRepository.upload(uploadSession, file.name, conversationContentType(file.name), bytes).fold(
                onSuccess = { uploaded ->
                    uploadSession = uploaded.first
                    attachmentUrl = FileUploadRepository.getDownloadUrl(uploaded.second.fileId).getOrNull()?.url
                    attachmentFilename = file.name
                    attachmentPreviewBytes = bytes.takeIf { conversationContentType(file.name).startsWith("image/", ignoreCase = true) }
                    if (attachmentUrl.isNullOrBlank()) uploadError = "Unable to prepare the attachment download link."
                },
                onFailure = { uploadError = it.message ?: "Unable to upload ${file.name}." }
            )
            uploading = false
        }
    }
    val repliesListState = remember(detail?.id) { androidx.compose.foundation.lazy.LazyListState() }
    LaunchedEffect(detail?.id, replies.size) {
        if (replies.isNotEmpty()) repliesListState.animateScrollToItem(replies.lastIndex)
    }
    LaunchedEffect(reply.isNotBlank()) {
        if (reply.isNotBlank() && replies.isNotEmpty()) repliesListState.animateScrollToItem(replies.lastIndex)
    }
    val canReply = detail != null && !detail.status.equals("CLOSED", ignoreCase = true)
    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, dialogWidthOverride = 820.dp, maxDialogHeight = 690.dp, viewportFraction = 0.98f, fillDialogHeight = true, isLoading = isLoading || isMutating || uploading, loadingTitle = when {
        uploading -> "Uploading attachment"
        isMutating -> "Sending reply"
        else -> "Loading conversation"
    }) {
        AppDialogHeader(
            title = "View Conversation & Replies",
            subtitle = "",
            onClose = onDismiss,
            titleColor = AppNavy,
            colors = AppDialogDefaultColors
        )
        AppDialogDivider()
        Column(Modifier.fillMaxWidth().weight(1f).padding(horizontal = 24.dp, vertical = 12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            if (errorMessage.isNotBlank() && detail == null) {
                AppDialogErrorMessage(errorMessage)
            } else if (detail != null) {
                val headerAttachmentName = detail.attachmentFilename.orEmpty().ifBlank { "conversation-attachment" }
                val headerAttachmentUrl = detail.attachmentUrl
                ConversationDetailMetadata(detail, headerAttachmentUrl) { url -> downloadAttachment(url, headerAttachmentName) }
                AppDialogDivider()
                Column(Modifier.fillMaxWidth().weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    AppDialogSectionLabel("Replies (${replies.size})", colors = AppDialogDefaultColors, fontSize = 16.sp)
                    if (replies.isEmpty()) {
                        Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.TopStart) {
                            Text("No replies yet.", color = AppMuted, fontSize = 13.sp)
                        }
                    } else {
                        DesktopAwareLazyColumn(
                            modifier = Modifier.fillMaxWidth().weight(1f),
                            state = repliesListState,
                            contentPadding = PaddingValues(end = 12.dp, bottom = 4.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            itemsIndexed(replies, key = { index, item -> item.id ?: "reply-$index" }) { index, item ->
                                val replyAttachmentName = item.attachmentFilename.orEmpty().ifBlank { "conversation-reply" }
                                val replyAttachmentUrl = item.attachmentUrl
                                ConversationReplyCard(item, replyAttachmentUrl) { url -> downloadAttachment(url, replyAttachmentName) }
                            }
                        }
                    }
                }
                if (canReply) {
                    AppDialogDivider()
                    AppDialogFieldColumn("Your Reply", required = true, colors = AppDialogDefaultColors) {
                        AppDialogTextField(reply, { reply = it }, "Write your reply...", colors = AppDialogDefaultColors, singleLine = false, fieldHeight = 64.dp, maxLength = 2000)
                        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Text("Attachment:", color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                            if (attachmentFilename == null) {
                                Text("Supported: JPG, PNG  ·  Max 5 MB", color = AppMuted, fontSize = 11.sp, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                            } else {
                                ClassConversationAttachmentPreviewRow(
                                    filename = attachmentFilename.orEmpty(),
                                    url = attachmentUrl,
                                    previewBytes = attachmentPreviewBytes,
                                    modifier = Modifier.weight(1f),
                                    onRemove = { attachmentUrl = null; attachmentFilename = null; attachmentPreviewBytes = null }
                                )
                            }
                            AppOutlinedButton(onClick = { attachmentPicker.launch() }, modifier = Modifier.height(40.dp), contentPadding = PaddingValues(horizontal = 12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(6.dp), border = BorderStroke(1.dp, AppPrimary)) {
                                Icon(painterResource(Res.drawable.cloud_upload), null, tint = AppPrimary, modifier = Modifier.size(16.dp)); Spacer(Modifier.width(7.dp)); Text(if (attachmentFilename == null) "Upload" else "Change", color = AppPrimary)
                            }
                        }
                        if (uploadError.isNotBlank()) AppDialogErrorMessage(uploadError)
                    }
                }
                if (mutationError.isNotBlank()) Text(mutationError, color = AppDanger, fontSize = 12.sp)
            }
        }
        AppDialogFooter(
            actions = if (canReply) listOf(
                AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Secondary, width = 100.dp),
                AppDialogFooterAction("Post Reply", { if (reply.isNotBlank() && !uploading) onReply(reply.trim(), attachmentUrl, attachmentFilename) }, AppDialogActionStyle.Primary, enabled = reply.isNotBlank() && !uploading && !isMutating, width = 120.dp)
            ) else listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Secondary, width = 100.dp)),
            colors = AppDialogDefaultColors,
            equalWeightButtons = false
        )
    }
}

@Composable
internal fun ConversationDetailMetadata(detail: com.schoolconnect.app.datasource.network.response.ClassConversationResponse, attachmentUrl: String? = detail.attachmentUrl, openUrl: (String) -> Unit) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp), verticalAlignment = Alignment.Top) {
            ConversationDetailMetaValue("Conversation ID", detail.conversationCode.orEmpty().ifBlank { detail.id.orEmpty().ifBlank { "—" } }, Modifier.weight(1f))
            ConversationDetailMetaValue("Posted By", detail.authorName.orEmpty().ifBlank { "—" }, Modifier.weight(1f))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp), verticalAlignment = Alignment.Top) {
            ConversationDetailMetaValue("Title", detail.title.orEmpty().ifBlank { "—" }, Modifier.weight(1f), emphasizeValue = true)
            ConversationDetailMetaValue("Posted On", formatApiConversationDate(detail.postedOn ?: detail.createdAt), Modifier.weight(1f))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp), verticalAlignment = Alignment.Top) {
            ConversationDetailMetaValue("Description", detail.description.orEmpty().ifBlank { "No description provided." }, Modifier.weight(1f))
            ConversationDetailMetaValue("Status", detail.status.orEmpty().ifBlank { "—" }, Modifier.weight(1f))
        }
        detail.attachmentFilename?.takeIf(String::isNotBlank)?.let { filename ->
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("Attachment:", color = AppMuted, fontSize = 12.sp, modifier = Modifier.width(118.dp))
                ClassConversationAttachmentPreviewRow(
                    filename = filename,
                    url = attachmentUrl,
                    sizeBytes = detail.attachmentSizeBytes,
                    modifier = Modifier.weight(1f),
                    onDownload = { attachmentUrl?.takeIf(String::isNotBlank)?.let(openUrl) }
                )
            }
        }
    }
}

@Composable
internal fun ConversationReplyCard(item: com.schoolconnect.app.datasource.network.response.ClassConversationReplyResponse, attachmentUrl: String? = item.attachmentUrl, openUrl: (String) -> Unit) {
    val sender = item.authorName.orEmpty().ifBlank { "—" }
    val avatarColors = listOf(Color(0xFFDCE5FB), Color(0xFFDDECCF), Color(0xFFF6DFD8), Color(0xFFEADFF4))
    val senderColorIndex = ((item.authorId?.takeIf(String::isNotBlank) ?: sender).hashCode() and Int.MAX_VALUE) % avatarColors.size
    Column(Modifier.fillMaxWidth().background(Color(0xFFF1F5FC), RoundedCornerShape(7.dp)).border(1.dp, Color(0xFFD7E0F0), RoundedCornerShape(7.dp)).padding(10.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(9.dp)) {
            Box(Modifier.size(30.dp).background(avatarColors[senderColorIndex], RoundedCornerShape(50)), contentAlignment = Alignment.Center) {
                Text(conversationInitials(sender), color = AppNavy, fontSize = 10.sp, fontWeight = FontWeight.SemiBold)
            }
            Text(sender, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            conversationAuthorRoleLabel(item.authorRole)?.let { ConversationAuthorRoleBadge(it) }
            Text("•  ${formatApiConversationDate(item.createdAt)}", color = AppMuted, fontSize = 10.sp)
        }
        Text(item.replyText.orEmpty().ifBlank { "—" }, color = AppNavy, fontSize = 12.sp, lineHeight = 17.sp, modifier = Modifier.padding(start = 39.dp))
        item.attachmentFilename?.takeIf(String::isNotBlank)?.let { filename ->
            ClassConversationAttachmentPreviewRow(
                filename = filename,
                url = attachmentUrl,
                modifier = Modifier.padding(start = 39.dp),
                onDownload = { attachmentUrl?.takeIf(String::isNotBlank)?.let(openUrl) }
            )
        }
    }
}

@Composable
internal fun ClassConversationAttachmentPreviewRow(
    filename: String,
    url: String? = null,
    previewBytes: ByteArray? = null,
    sizeBytes: Int? = null,
    modifier: Modifier = Modifier,
    onDownload: (() -> Unit)? = null,
    onRemove: (() -> Unit)? = null
) {
    var loadedPreview by remember(filename, url) { mutableStateOf<ByteArray?>(null) }
    LaunchedEffect(filename, url) {
        loadedPreview = null
        val previewUrl = url?.takeIf(String::isNotBlank)
        if (previewUrl != null && conversationContentType(filename).startsWith("image/", ignoreCase = true)) {
            loadedPreview = FileUploadRepository.downloadBytes(previewUrl).getOrNull()
        }
    }
    val imageBytes = previewBytes ?: loadedPreview
    val image = remember(filename, imageBytes) { imageBytes?.let(::decodeImageBitmap) }
    val kind = when {
        conversationContentType(filename).startsWith("image/", ignoreCase = true) -> "Image"
        filename.substringAfterLast('.', "").equals("pdf", ignoreCase = true) -> "PDF"
        else -> filename.substringAfterLast('.', "FILE").uppercase().ifBlank { "FILE" }
    }
    Row(
        modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        if (image != null) {
            Image(image, filename, Modifier.size(48.dp).background(Color.White, RoundedCornerShape(6.dp)), contentScale = ContentScale.Crop)
        } else {
            Box(Modifier.size(38.dp).background(Color(0xFFEAF0FF), RoundedCornerShape(6.dp)), contentAlignment = Alignment.Center) {
                Icon(painterResource(Res.drawable.document), filename, tint = AppPrimary, modifier = Modifier.size(20.dp))
            }
        }
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(filename, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Medium, maxLines = 1, overflow = TextOverflow.Ellipsis)
            val sizeLabel = sizeBytes?.takeIf { it > 0 }?.let(::formatConversationAttachmentSize)
            Text(listOfNotNull(kind, sizeLabel).joinToString("  •  "), color = AppMuted, fontSize = 11.sp, maxLines = 1)
        }
        when {
            onRemove != null -> IconButton(onClick = onRemove, modifier = Modifier.size(32.dp)) {
                Icon(painterResource(Res.drawable.close_x), "Remove attachment", tint = AppMuted, modifier = Modifier.size(16.dp))
            }
            onDownload != null -> IconButton(onClick = onDownload, modifier = Modifier.size(32.dp), enabled = !url.isNullOrBlank()) {
                Icon(painterResource(Res.drawable.download), "Download $filename", tint = if (url.isNullOrBlank()) AppMuted else AppPrimary, modifier = Modifier.size(18.dp))
            }
        }
    }
}

private fun conversationInitials(name: String): String = name.split(Regex("\\s+")).filter(String::isNotBlank).take(2).joinToString("") { it.first().uppercase() }.ifBlank { "—" }

@Composable
internal fun ConversationDetailMetaValue(label: String, value: String, modifier: Modifier, emphasizeValue: Boolean = false) {
    Row(modifier, horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.Top) {
        Text(label, color = AppMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.width(118.dp))
        Text(value, color = AppNavy, fontSize = 12.sp, fontWeight = if (emphasizeValue) FontWeight.SemiBold else FontWeight.Normal, lineHeight = 17.sp, modifier = Modifier.weight(1f))
    }
}

private fun apiConversationClassLabel(className: String?, sectionName: String?): String =
    listOfNotNull(className?.trim()?.takeIf { it.isNotBlank() }, sectionName?.trim()?.takeIf { it.isNotBlank() }).joinToString("-").ifBlank { "Class based on selected child" }

private fun formatApiConversationDate(value: String?): String {
    val raw = value?.trim().orEmpty()
    if (raw.isBlank()) return "—"
    val parts = raw.substringBefore('T').substringBefore(' ').split('-')
    if (parts.size != 3) return raw.replace('T', ' ').take(16)
    val year = parts[0].toIntOrNull() ?: return raw.replace('T', ' ').take(16)
    val month = parts[1].toIntOrNull()?.takeIf { it in 1..12 } ?: return raw.replace('T', ' ').take(16)
    val day = parts[2].toIntOrNull() ?: return raw.replace('T', ' ').take(16)
    val monthLabel = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[month - 1]
    val time = raw.substringAfter('T', raw.substringAfter(' ', "")).take(5).takeIf { it.length == 5 }?.let { value ->
        val hour = value.substringBefore(':').toIntOrNull() ?: return@let value
        val minute = value.substringAfter(':').toIntOrNull() ?: return@let value
        "${(hour % 12).let { if (it == 0) 12 else it }}:${minute.toString().padStart(2, '0')} ${if (hour < 12) "AM" else "PM"}"
    }
    return buildString {
        append(day.toString().padStart(2, '0')).append(' ').append(monthLabel).append(' ').append(year)
        if (time != null) append(", ").append(time)
    }
}

@Composable
private fun ClassConversationViewDialog(row: ClassConversation, adminMode: Boolean, onDismiss: () -> Unit) {
    var reply by remember(row.id) { mutableStateOf("") }
    var attachmentAdded by remember(row.id) { mutableStateOf(false) }
    val replies = listOf(
        Triple("AV", "Anjali Verma", "The class teacher mentioned that costumes should be brought from home in a labelled bag."),
        Triple("RM", "Rahul Mehta", "Yes, and children should wear their regular uniform to school."),
        Triple("PS", "Priya Sharma", "Thank you for confirming.")
    )

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, maxDialogHeight = 860.dp) {
        AppDialogHeader(
            title = if (adminMode) "View Conversation & Reply" else "View Conversation & Replies",
            subtitle = if (adminMode) "${row.id}  ·  Review the discussion and share a reply" else "Review the discussion and share a reply",
            onClose = onDismiss,
            icon = Res.drawable.message,
            iconContentDescription = "Conversation",
            colors = AppDialogDefaultColors
        )
        Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(horizontal = 24.dp, vertical = 12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            ClassConversationMetadata(row)
            AppDialogDivider()
            AppDialogSectionLabel("Replies (${replies.size})", colors = AppDialogDefaultColors, fontSize = 16.sp)
            replies.forEachIndexed { index, item ->
                Row(Modifier.fillMaxWidth().background(Color(0xFFF1F5FC), RoundedCornerShape(7.dp)).border(1.dp, Color(0xFFD7E0F0), RoundedCornerShape(7.dp)).padding(8.dp), verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Box(Modifier.size(28.dp).background(if (index == 1) Color(0xFFDFF0C8) else Color(0xFFE0E8FB), RoundedCornerShape(50)), contentAlignment = Alignment.Center) { Text(item.first, color = AppNavy, fontSize = 10.sp, fontWeight = FontWeight.Bold) }
                    Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) { Text(item.second, color = AppNavy, fontSize = 11.sp, fontWeight = FontWeight.Bold); Text("• 01 Aug 2026, ${if (index == 0) "10:32" else if (index == 1) "11:05" else "11:18"} AM", color = AppMuted, fontSize = 10.sp) }
                        Text(item.third, color = AppNavy, fontSize = 11.sp)
                    }
                }
            }
            AppDialogDivider()
            AppDialogFieldColumn("Your Reply", required = true, colors = AppDialogDefaultColors) {
                AppDialogTextField(reply, { reply = it }, "Write your reply...", colors = AppDialogDefaultColors, singleLine = false, fieldHeight = 68.dp, maxLength = 2000)
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("Attachment:", color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                    Text(if (attachmentAdded) "conversation-reply.pdf · 248 KB" else "Supported: PDF, JPG, PNG  ·  Max 5 MB", color = AppMuted, fontSize = 11.sp, modifier = Modifier.weight(1f))
                    AppOutlinedButton(onClick = { attachmentAdded = !attachmentAdded }, modifier = Modifier.height(38.dp), contentPadding = PaddingValues(horizontal = 12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(7.dp)) {
                        Icon(painterResource(Res.drawable.paperclip), null, tint = AppPrimary, modifier = Modifier.size(16.dp)); Spacer(Modifier.width(5.dp)); Text("Upload", color = AppPrimary, fontSize = 12.sp)
                    }
                }
            }
        }
        AppDialogFooter(actions = listOf(AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Secondary, width = 120.dp), AppDialogFooterAction("Post Reply", { if (reply.isNotBlank()) reply = "" }, AppDialogActionStyle.Primary, width = 140.dp)), colors = AppDialogDefaultColors, equalWeightButtons = false)
    }
}

@Composable
private fun ClassConversationMetadata(row: ClassConversation) {
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp)) {
            ConversationMetaValue("Conversation ID", row.id, Modifier.weight(1f))
            ConversationMetaValue("Title", if (row.id == "CC-0001") "Annual Day costume requirements" else row.title, Modifier.weight(2.8f))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp)) {
            ConversationMetaValue("Posted By", "Priya Sharma", Modifier.weight(1f))
            ConversationMetaValue("Posted On", "01 Aug 2026, 10:15 AM", Modifier.weight(1f))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp)) {
            ConversationMetaValue("Description", "Could someone please confirm whether the children need to bring their costumes from home for the Annual Day rehearsal?", Modifier.weight(1f))
        }
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("Attachment:", color = AppMuted, fontSize = 12.sp)
            Icon(painterResource(Res.drawable.document), null, tint = AppDanger, modifier = Modifier.size(17.dp))
            Text("Annual_Day_Guidelines.pdf", color = AppPrimary, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            Icon(painterResource(Res.drawable.download), "Download attachment", tint = AppPrimary, modifier = Modifier.size(16.dp))
        }
    }
}

@Composable
private fun ConversationMetaValue(label: String, value: String, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) { Text(label, color = AppMuted, fontSize = 12.sp); Text(value, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.Medium) }
}

@Composable
private fun EditConversationDetailLoadingDialog(isLoading: Boolean, errorMessage: String, onRetry: () -> Unit, onDismiss: () -> Unit) {
    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Standard,
        dialogWidthOverride = 560.dp,
        maxDialogHeight = 300.dp,
        dismissOnClickOutside = false
    ) {
        AppDialogHeader(
            title = "Edit Conversation",
            subtitle = "Loading the selected conversation details",
            onClose = onDismiss,
            icon = Res.drawable.message,
            iconContentDescription = "Conversation",
            colors = AppDialogDefaultColors
        )
        AppDialogDivider()
        Box(
            Modifier.fillMaxWidth().height(100.dp).padding(horizontal = 24.dp, vertical = 16.dp),
            contentAlignment = Alignment.Center
        ) {
            if (isLoading) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    CircularProgressIndicator(color = AppPrimary, strokeWidth = 2.dp, modifier = Modifier.size(22.dp))
                    Text("Loading conversation details…", color = AppMuted, fontSize = 13.sp)
                }
            } else {
                AppDialogErrorMessage(errorMessage.ifBlank { "Unable to load this conversation." })
            }
        }
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Close", onDismiss, AppDialogActionStyle.Secondary, width = 100.dp),
                AppDialogFooterAction("Retry", onRetry, AppDialogActionStyle.Primary, enabled = !isLoading, width = 100.dp)
            ),
            colors = AppDialogDefaultColors,
            equalWeightButtons = false
        )
    }
}

@Composable
private fun StartConversationDialog(initial: ClassConversation? = null, initialDetail: com.schoolconnect.app.datasource.network.response.ClassConversationResponse? = null, classGroupLabel: String = "Class —", onDismiss: () -> Unit, onSubmit: ((String, String, String?, String?, Int?, Boolean) -> Unit)? = null) {
    val isEditing = initial != null || initialDetail != null
    val initialConversationId = initialDetail?.id ?: initial?.id
    var title by remember(initialConversationId) { mutableStateOf(initialDetail?.title ?: initial?.title.orEmpty()) }
    var description by remember(initialConversationId) { mutableStateOf(initialDetail?.description.orEmpty()) }
    var hasSubmitted by remember { mutableStateOf(false) }
    var attachmentUrl by remember(initialConversationId) { mutableStateOf(if (initialDetail != null) initialDetail.attachmentUrl else initial?.attachmentUrl) }
    var attachmentFilename by remember(initialConversationId) { mutableStateOf(if (initialDetail != null) initialDetail.attachmentFilename else initial?.attachmentFilename) }
    var attachmentSizeBytes by remember(initialConversationId) { mutableStateOf(if (initialDetail != null) initialDetail.attachmentSizeBytes else initial?.attachmentSizeBytes) }
    var attachmentChanged by remember(initialConversationId) { mutableStateOf(false) }
    var isUploading by remember(initialConversationId) { mutableStateOf(false) }
    var attachmentError by remember(initialConversationId) { mutableStateOf("") }
    val coroutineScope = rememberCoroutineScope()
    val uploadSession = remember(initialConversationId) { FileUploadRepository.newSession("CLASS_CONVERSATION").copy(recordId = initial?.backendId ?: initialDetail?.id) }
    var currentUploadSession by remember(initialConversationId) { mutableStateOf(uploadSession) }
    val attachmentPicker = rememberFilePickerLauncher(type = PickerType.File(listOf("pdf", "doc", "docx", "jpg", "jpeg", "png")), mode = PickerMode.Multiple(maxItems = 1), title = "Select conversation attachment") { files ->
        val file = files?.firstOrNull()
        if (file != null) coroutineScope.launch {
            isUploading = true
            attachmentError = ""
            val bytes = runCatching { file.readBytes() }.getOrNull()
            if (bytes == null || bytes.isEmpty()) attachmentError = "Unable to read ${file.name}."
            else if (bytes.size > 10 * 1024 * 1024) attachmentError = "Attachment must be 10 MB or smaller."
            else FileUploadRepository.upload(currentUploadSession, file.name, conversationContentType(file.name), bytes).fold(
                onSuccess = { uploaded ->
                    currentUploadSession = uploaded.first
                    attachmentUrl = FileUploadRepository.getDownloadUrl(uploaded.second.fileId).getOrNull()?.url
                    attachmentFilename = file.name
                    attachmentSizeBytes = bytes.size
                    attachmentChanged = true
                },
                onFailure = { attachmentError = it.message ?: "Unable to upload ${file.name}." }
            )
            isUploading = false
        }
    }
    val titleError = if (hasSubmitted && title.isBlank()) "Conversation title is required." else ""
    val descriptionError = if (hasSubmitted && description.isBlank()) "Description is required." else ""
    val canSubmit = title.isNotBlank() && description.isNotBlank()

    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Standard, dialogWidthOverride = 780.dp, maxDialogHeight = 720.dp) {
        AppDialogHeader(
            title = if (isEditing) "Edit Conversation" else "Start Conversation",
            subtitle = if (isEditing) "Update your conversation details" else "Post a query or discussion for parents in your child's class.",
            onClose = onDismiss,
            icon = Res.drawable.message,
            iconContentDescription = "Conversation",
            colors = AppDialogDefaultColors
        )
        Column(
            Modifier.fillMaxWidth().weight(1f).verticalScroll(rememberScrollState()).padding(horizontal = 28.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(18.dp)
        ) {
            AppDialogFieldColumn("Class Group", colors = AppDialogDefaultColors) {
                AppDialogTextField(classGroupLabel, {}, classGroupLabel, colors = AppDialogDefaultColors, readOnly = true)
                Text("Based on the selected child", color = AppMuted, fontSize = 12.sp)
            }
            AppDialogLabeledTextField(
                label = "Title",
                value = title,
                onValueChange = { title = it },
                placeholder = "Enter conversation title",
                required = true,
                error = titleError,
                colors = AppDialogDefaultColors,
                maxLength = 160,
                trailingIcon = { Text("${title.length}/160", color = AppMuted, fontSize = 11.sp) }
            )
            AppDialogFieldColumn("Description", required = true, error = descriptionError, colors = AppDialogDefaultColors) {
                AppDialogTextField(
                    value = description,
                    onValueChange = { description = it },
                    placeholder = "Enter your query or discussion",
                    colors = AppDialogDefaultColors,
                    error = descriptionError,
                    fieldHeight = 120.dp,
                    singleLine = false,
                    maxLength = 2000,
                    trailingIcon = { Text("${description.length}/2000", color = AppMuted, fontSize = 11.sp) }
                )
            }
            AppDialogFieldColumn("Attachments", helperText = "Optional", colors = AppDialogDefaultColors) {
                if (attachmentFilename != null) {
                    Row(
                        Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Icon(painterResource(Res.drawable.document), null, tint = AppDanger, modifier = Modifier.size(24.dp))
                        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                            Text(attachmentFilename ?: "Attachment", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                            Text(formatConversationAttachmentSize(attachmentSizeBytes), color = AppMuted, fontSize = 12.sp)
                        }
                        AppOutlinedButton(onClick = { attachmentPicker.launch() }, modifier = Modifier.height(40.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(7.dp)) {
                            Icon(painterResource(Res.drawable.document), null, Modifier.size(16.dp), tint = AppPrimary)
                            Spacer(Modifier.width(6.dp))
                            Text("Upload", fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                        }
                        AppOutlinedButton(onClick = { attachmentUrl = null; attachmentFilename = null; attachmentSizeBytes = null; attachmentChanged = true }, modifier = Modifier.size(40.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppDanger), shape = RoundedCornerShape(7.dp), contentPadding = PaddingValues(0.dp)) {
                            Icon(painterResource(Res.drawable.trash), "Remove attachment", Modifier.size(17.dp), tint = AppDanger)
                        }
                    }
                } else {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(
                            Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(10.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            AppOutlinedButton(
                                onClick = { attachmentPicker.launch() },
                                modifier = Modifier.width(170.dp).height(44.dp),
                                contentPadding = PaddingValues(horizontal = 12.dp),
                                colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary),
                                shape = RoundedCornerShape(8.dp)
                            ) {
                                Icon(painterResource(Res.drawable.cloud_upload), null, tint = AppPrimary, modifier = Modifier.size(18.dp))
                                Spacer(Modifier.width(8.dp))
                                Text("Upload Files", color = AppPrimary, fontWeight = FontWeight.SemiBold)
                            }
                            Spacer(Modifier.width(14.dp))
                            Text("No files selected", color = AppMuted, fontSize = 13.sp)
                        }
                        Text("Supported: PDF, DOC, DOCX, JPG, PNG (Max 10 MB per file)", color = AppMuted, fontSize = 12.sp)
                    }
                }
            }
        }
        if (attachmentError.isNotBlank()) Text(attachmentError, color = AppDanger, fontSize = 12.sp, modifier = Modifier.padding(horizontal = 28.dp))
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", onDismiss, AppDialogActionStyle.Secondary, width = 140.dp),
                AppDialogFooterAction(if (isEditing) "Save" else "Post Conversation", { hasSubmitted = true; if (canSubmit && !isUploading) { onSubmit?.invoke(title, description, attachmentUrl, attachmentFilename, attachmentSizeBytes, attachmentChanged) ?: onDismiss() } }, AppDialogActionStyle.Primary, width = if (isEditing) 140.dp else 180.dp)
            ),
            colors = AppDialogDefaultColors,
            equalWeightButtons = false
        )
    }
}

private fun conversationContentType(filename: String): String = when (filename.substringAfterLast('.', "").lowercase()) {
    "pdf" -> "application/pdf"
    "doc" -> "application/msword"
    "docx" -> "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    "jpg", "jpeg" -> "image/jpeg"
    "png" -> "image/png"
    else -> "application/octet-stream"
}

private fun formatConversationAttachmentSize(bytes: Int?): String = when {
    bytes == null || bytes <= 0 -> "Size unavailable"
    bytes >= 1024 * 1024 -> "${bytes / (1024 * 1024)} MB"
    else -> "${(bytes / 1024).coerceAtLeast(1)} KB"
}

@Composable
private fun ClassConversationFilters(search: String, period: String, adminMode: Boolean, classFilter: String, sectionFilter: String, onSearchChanged: (String) -> Unit, onPeriodChanged: (String) -> Unit, onClassChanged: (String) -> Unit, onSectionChanged: (String) -> Unit, onClear: () -> Unit) {
    AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        Column(Modifier.fillMaxWidth().padding(16.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(if (adminMode) 24.dp else 30.dp), verticalAlignment = Alignment.Bottom) {
                Column(Modifier.width(if (adminMode) 300.dp else 340.dp), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                    Text("Search by Conversation ID / Title", color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    AdminListSearchField(search, onSearchChanged, "Search...", width = if (adminMode) 300.dp else 340.dp, surfaceColor = Color.White, borderColor = AppBorder, textColor = AppNavy)
                }
                if (adminMode) {
                    ClassConversationDropdown("Class", classFilter, listOf("All Classes", "4-A", "5-A", "5-B", "6-A", "6-B", "7-B"), Modifier.width(170.dp), onClassChanged)
                    ClassConversationDropdown("Section", sectionFilter, listOf("All Sections", "A", "B"), Modifier.width(170.dp), onSectionChanged)
                }
                ClassConversationDropdown("Period", period, listOf("All", "Today", "Last 7 Days", "This Month", "Custom Range"), Modifier.width(255.dp), onPeriodChanged)
                Spacer(Modifier.weight(1f))
                AppOutlinedButton(
                    onClick = onClear,
                    modifier = Modifier.width(116.dp).height(44.dp),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary),
                    shape = RoundedCornerShape(8.dp)
                ) {
                    Icon(painterResource(Res.drawable.refresh_cw), null, Modifier.size(17.dp), tint = AppPrimary)
                    Spacer(Modifier.width(6.dp))
                    Text("Clear", color = AppPrimary, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                }
            }
        }
    }
}

@Composable
private fun ClassConversationDropdown(label: String, value: String, options: List<String>, modifier: Modifier, onSelected: (String) -> Unit) =
    AppLabeledDropdownField(label, value, options, onSelected, modifier, includeAllOption = false)

@Composable
private fun ClassConversationHeader(adminMode: Boolean = false) {
    Row(Modifier.fillMaxWidth().background(Color(0xFFFBFCFF)).padding(horizontal = 24.dp, vertical = 14.dp), verticalAlignment = Alignment.CenterVertically) {
        AdminListTableBodyText("Conversation ID", Modifier.weight(1.05f), fontWeight = FontWeight.Bold)
        AdminListTableBodyText("Title", Modifier.weight(1.7f), fontWeight = FontWeight.Bold)
        if (adminMode) AdminListTableBodyText("Class", Modifier.weight(.75f), fontWeight = FontWeight.Bold)
        AdminListTableBodyText("Posted By", Modifier.weight(1.6f), fontWeight = FontWeight.Bold)
        AdminListTableBodyText("Posted On", Modifier.weight(1.25f), fontWeight = FontWeight.Bold)
        Box(Modifier.weight(.8f), contentAlignment = Alignment.Center) { AdminListTableBodyText("Replies", Modifier, fontWeight = FontWeight.Bold) }
        AdminListTableBodyText("Status", Modifier.weight(.7f), fontWeight = FontWeight.Bold)
        Box(Modifier.weight(.7f), contentAlignment = Alignment.Center) { AdminListTableBodyText("Actions", Modifier, fontWeight = FontWeight.Bold) }
    }
}

@Composable
private fun ClassConversationApiHeader(state: ClassConversationsUiState, adminMode: Boolean, onEvent: (ClassConversationsUiEvent) -> Unit) {
    val sort = state.sortField
    AdminListSortHeaderRow(
        columns = buildList {
            add(AdminListSortColumn("Conversation ID", 1.05f, sort == ClassConversationsSortField.ID, state.sortAscending) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.ID)) })
            add(AdminListSortColumn("Title", 1.7f, sort == ClassConversationsSortField.TITLE, state.sortAscending) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.TITLE)) })
            if (adminMode) add(AdminListSortColumn("Class", .75f, sort == ClassConversationsSortField.CLASS, state.sortAscending) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.CLASS)) })
            add(AdminListSortColumn("Posted By", 1.6f, sort == ClassConversationsSortField.POSTED_BY, state.sortAscending) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.POSTED_BY)) })
            add(AdminListSortColumn("Posted On", 1.25f, sort == ClassConversationsSortField.POSTED_ON, state.sortAscending) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.POSTED_ON)) })
            add(AdminListSortColumn("Replies", .8f, sort == ClassConversationsSortField.REPLIES, state.sortAscending, alignCenter = true) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.REPLIES)) })
            add(AdminListSortColumn("Status", .7f, sort == ClassConversationsSortField.STATUS, state.sortAscending) { onEvent(ClassConversationsUiEvent.SortChanged(ClassConversationsSortField.STATUS)) })
        },
        actionsLabel = "Actions",
        actionsWeight = .7f,
        horizontalPadding = 24.dp,
        actionsAlignment = Alignment.Center
    )
}

internal fun conversationAuthorRoleLabel(roleCode: String?): String? = when (roleCode?.trim()?.uppercase()) {
    "SCHOOL_TEACHER", "TEACHER" -> "Teacher"
    "SCHOOL_ADMIN", "ADMIN" -> "School Admin"
    else -> null
}

@Composable
internal fun ConversationAuthorRoleBadge(label: String) {
    Box(
        Modifier
            .background(Color(0xFFEAF2FF), RoundedCornerShape(4.dp))
            .border(1.dp, Color(0xFFBFD4FF), RoundedCornerShape(4.dp))
            .padding(horizontal = 6.dp, vertical = 2.dp)
    ) {
        Text(label, color = AppPrimary, fontSize = 10.sp, fontWeight = FontWeight.SemiBold)
    }
}
